import { Component, OnInit, Input } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { IrrigationDataService } from './irrigation-data.service';
import { IrrigationData } from './irrigation-data.interface';
import { FieldDashboardDataService } from '../field-dashboard-data.service';

@Component({
  selector: 'app-irrigation-chart',
  templateUrl: './irrigation-chart.component.html',
  styleUrls: ['./irrigation-chart.component.css'],
  standalone: false
})
export class IrrigationChartComponent implements OnInit {
  @Input() fieldId: string;
  chartOption: EChartsOption = {};
  summaryChartOption: EChartsOption = {};
  private subscription: any;

  constructor(
    private irrigationService: IrrigationDataService,
    private dataService: FieldDashboardDataService
  ) { }

  ngOnInit(): void {
    this.initializeChart();
    this.subscription = this.dataService.fieldData$.subscribe(data => {
      if (data.fieldId && data.startDate && data.endDate) {
        this.fieldId = data.fieldId;
        this.loadIrrigationData(data.startDate, data.endDate);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  initializeChart(): void {
    this.chartOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: ['Median Qty', 'Sum Qty', 'Total']
      },
      xAxis: {
        type: 'category',
        data: []
      },
      yAxis: {
        type: 'value',
        name: 'Quantity'
      },
      series: [
        {
          name: 'Median Qty',
          type: 'bar',
          stack: 'total',
          data: []
        },
        {
          name: 'Sum Qty',
          type: 'bar',
          stack: 'total',
          data: []
        },
        {
          name: 'Total',
          type: 'bar',
          stack: 'total',
          data: []
        }
      ]
    };
  }

  loadIrrigationData(startDate: Date, endDate: Date): void {
    const fromDate = this.formatDateForApi(startDate);
    const toDate = this.formatDateForApi(endDate);
    
    this.irrigationService.getIrrigationStats(fromDate, toDate, Number(this.fieldId))
      .subscribe({
        next: (data: IrrigationData[]) => {
          console.log('API Response:', data);
          this.updateChart(data);
        },
        error: (error) => {
          console.error('Error fetching irrigation data:', error);
        }
      });
  }

  updateChart(data: IrrigationData[]): void {
    // Group data by date and sort by startTime
    const dateGroups = data.reduce((acc, item) => {
      const formattedDate = this.formatDate(item.date);
      if (!acc[formattedDate]) {
        acc[formattedDate] = [];
      }
      acc[formattedDate].push(item);
      return acc;
    }, {} as { [key: string]: IrrigationData[] });

    // Sort runs within each date by startTime
    Object.keys(dateGroups).forEach(date => {
      dateGroups[date].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    });

    const allDates = Object.keys(dateGroups);
    const series: any[] = [];
    const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4'];

    // Find max number of runs in a single day
    const maxRuns = Math.max(...Object.values(dateGroups).map(runs => runs.length));

    // Create a series for each run position (1st run, 2nd run, etc.)
    for (let runIdx = 0; runIdx < maxRuns; runIdx++) {
      const seriesData = allDates.map(date => {
        const runs = dateGroups[date];
        if (runs[runIdx]) {
          return {
            value: runs[runIdx].medianOfQtyGivenConvertedFromTime,
            itemData: runs[runIdx]
          };
        }
        return null;
      });

      series.push({
        name: 'Irrigation Run',
        type: 'bar',
        stack: 'total',
        itemStyle: {
          color: colors[runIdx % colors.length]
        },
        data: seriesData
      });
    }

    this.chartOption = {
      ...this.chartOption,
      grid: {
        left: '10%',
        right: '10%',
        top: '60px',
        bottom: '60px',
        containLabel: true
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const seriesName = params.seriesName;

          if (seriesName === 'Temperature') {
            return `<strong>Date:</strong> ${params.name}<br/><strong>Temperature:</strong> ${params.value != null ? params.value.toFixed(1) + '°C' : 'N/A'}`;
          }
          if (seriesName === 'ET0') {
            return `<strong>Date:</strong> ${params.name}<br/><strong>ET0:</strong> ${params.value != null ? params.value.toFixed(2) + ' mm' : 'N/A'}`;
          }
          if (seriesName === 'Precipitation') {
            return `<strong>Date:</strong> ${params.name}<br/><strong>Precipitation:</strong> ${params.value != null ? params.value.toFixed(1) + 'mm' : 'N/A'}`;
          }

          const item = params.data?.itemData;
          if (!item) return '';
          const date = this.formatDate(item.date);
          const runs = dateGroups[date] || [];
          const totalSum = runs.reduce((sum, run) => sum + run.medianOfQtyGivenConvertedFromTime, 0);
          return `
            <strong>Date:</strong> ${item.date}<br/>
            <strong>Start Time:</strong> ${item.startTime}<br/>
            <strong>Irrigation:</strong> ${item.medianOfQtyGivenConvertedFromTime.toFixed(2)} mm<br/>
            <strong>Duration:</strong> ${item.sumOfQtyGivenTime} min<br/>
            <strong>Day Total:</strong> ${totalSum.toFixed(2)} mm
          `;
        }
      },
      legend: {
        data: ['Temperature', 'ET0', 'Precipitation']
      },
      xAxis: {
        type: 'category',
        data: allDates
      },
      yAxis: [
        {
          type: 'value',
          name: 'Irrigation (mm)',
          nameLocation: 'middle',
          nameGap: 50,
          nameRotate: 90
        },
        {
          type: 'value',
          name: 'Temp (°C) / ET0 (mm)',
          nameLocation: 'middle',
          nameGap: 50,
          nameRotate: -90,
          position: 'right'
        }
      ],
      series: [
        ...series,
        {
          name: 'Temperature',
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { color: '#ee6666', width: 2 },
          itemStyle: { color: '#ee6666' },
          data: allDates.map(date => {
            const runs = dateGroups[date];
            const temp = runs.find(r => r.avgHcAirTemperature != null)?.avgHcAirTemperature;
            return temp ?? null;
          })
        },
        {
          name: 'ET0',
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          symbol: 'diamond',
          symbolSize: 6,
          lineStyle: { color: '#73c0de', width: 2 },
          itemStyle: { color: '#73c0de' },
          data: allDates.map(date => {
            const runs = dateGroups[date];
            const et0 = runs.find(r => r.sumEt0 != null)?.sumEt0;
            return et0 ?? null;
          })
        },
        {
          name: 'Precipitation',
          type: 'bar',
          yAxisIndex: 1,
          barWidth: 8,
          itemStyle: { color: '#5470c6', opacity: 0.5 },
          data: allDates.map(date => {
            const runs = dateGroups[date];
            const precip = runs.find(r => r.sumPrecipitation != null)?.sumPrecipitation;
            return precip ?? null;
          })
        }
      ]
    };

    // Summary chart - ET0 vs Total Irrigation as percentage
    const totalEt0 = data.reduce((sum, d) => sum + (d.sumEt0 || 0), 0);
    const totalIrrigation = data.reduce((sum, d) => sum + (d.medianOfQtyGivenConvertedFromTime || 0), 0);
    const irrigationPercent = totalEt0 > 0 ? (totalIrrigation / totalEt0) * 100 : 0;

    this.summaryChartOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          if (!Array.isArray(params)) return '';
          return params.map((p: any) => `<strong>${p.name}:</strong> ${p.value.toFixed(2)} mm`).join('<br/>');
        }
      },
      grid: { left: '25%', right: '15%', top: '10px', bottom: '10px' },
      xAxis: { type: 'value', show: false },
      yAxis: {
        type: 'category',
        data: ['Sum of ET0', 'Sum of Irrigation']
      },
      series: [{
        type: 'bar',
        data: [
          { value: totalEt0, itemStyle: { color: '#ee6666' } },
          { value: totalIrrigation, itemStyle: { color: '#5470c6' } }
        ],
        label: { show: true, position: 'right', formatter: (p: any) => `${p.value.toFixed(2)} mm${p.dataIndex === 1 ? ' (' + irrigationPercent.toFixed(1) + '% of ET0)' : ''}` }
      }]
    };
  }

  formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${day} ${month}`;
  }
}
